// Bees: how BusyBee.Day draws its bots (one bee per bot) and itself (the queen).
// Seen from the front: the head is a honeycomb cell in the bot's color with a dark visor,
// golden eyes in it and stripes below. Flat colors only. Expressions come from the eyes'
// shape and the bee's motion shows what it does (after Grok Bot: simple shapes, expressive
// eyes, no separate status dot).
//
// Pure functions that return SVG text, so the web (Bee.jsx), the desktop app and the GNOME
// Shell extension draw the same bee. The extension keeps an exact copy of this file in
// desktop/gnome-extension/busybee@busybee.day/lib/beeSvg.js (a test compares them).
// Geometry is in a 120 x 120 view box; the bee itself fills 10..110.

export const HONEY = '#F5B83D';
const PUPIL = '#1A1203';
const INK = '#17130c';

/**
 * Species tell bots apart beside their color: the size and roundness of the cell, where
 * the stripes sit (0 = just below the visor, 1 = at the bottom) and how they look.
 */
export const SPECIES = {
  kralovna: { R: 33, round: 7, stripes: [0.3, 0.52, 0.74], t: 0.11, kind: 'ink', crown: true },
  robotnica: { R: 32, round: 7, stripes: [0.32, 0.55, 0.78], t: 0.11, kind: 'ink' },
  cmeliak: { R: 34, round: 12, stripes: [0.42, 0.74], t: 0.17, kind: 'ink' },
  orchidea: { R: 32, round: 7, stripes: [0.4, 0.7], t: 0.11, kind: 'tint' },
  drevarka: { R: 32, round: 7, stripes: [0.38, 0.64], t: 0.12, kind: 'inverse', body: '#2b2536', wingTint: true },
  murarka: { R: 32, round: 9, stripes: [0.3, 0.52, 0.74], t: 0.07, kind: 'light' },
};

// The species bots get (the queen is BusyBee's own).
export const BOT_SPECIES = ['robotnica', 'cmeliak', 'orchidea', 'drevarka', 'murarka'];
export const STATES = ['idle', 'focus', 'work', 'needs', 'done', 'fail', 'sleep'];
export const ACCESSORIES = ['none', 'glasses', 'headphones', 'beret', 'bowtie', 'flower', 'crown'];

// What the bee is drawn with around its own colors. The web passes CSS variables (bees.css
// sets them per theme); the GNOME extension, which can't read CSS, passes the dark set.
export const PALETTES = {
  css: { head: 'var(--bb-head)', wing: 'var(--bb-wing)', wingEdge: 'var(--bb-wing-edge)', glass: 'var(--bb-glass)', zz: 'var(--bb-zz)' },
  dark: { head: '#38332b', wing: 'rgba(225,232,255,0.22)', wingEdge: 'rgba(255,255,255,0.7)', glass: '#e9e9ef', zz: '#b9b9c1' },
  light: { head: '#221d15', wing: 'rgba(110,120,150,0.16)', wingEdge: 'rgba(60,70,95,0.5)', glass: '#2a2722', zz: '#8a877f' },
};

// Where the wings and antennae turn (CSS transform-origin and the shell's pivot points).
export const PIVOTS = { body: [60, 66], wingL: [46, 50], wingR: [74, 50], antL: [53, 40], antR: [67, 40] };

const r2 = (n) => Math.round(n * 100) / 100;

/** Relative luminance of a #RRGGBB color (WCAG). */
export function luminance(hex) {
  const h = String(hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return 0.5;
  const [r, g, b] = [0, 2, 4]
    .map((i) => parseInt(h.slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** #RRGGBB mixed toward another color by t (0..1); unreadable input stays as it is. */
export function mix(a, b, t) {
  const ok = (x) => /^#[0-9a-fA-F]{6}$/.test(String(x));
  if (!ok(a) || !ok(b)) return a;
  const parts = (x) => [1, 3, 5].map((i) => parseInt(x.slice(i, i + 2), 16));
  const A = parts(a);
  const B = parts(b);
  return `#${A.map((v, i) => Math.round(v + (B[i] - v) * t).toString(16).padStart(2, '0')).join('')}`;
}

/** The color of the stripes on a body of this color. */
export function stripeColor(species, color) {
  const sp = SPECIES[species] || SPECIES.robotnica;
  if (sp.kind === 'inverse') return color;
  if (sp.kind === 'tint') return mix(color, '#ffffff', 0.45);
  if (sp.kind === 'light') return 'rgba(255,255,255,0.5)';
  return luminance(color) < 0.18 ? 'rgba(255,255,255,0.34)' : INK;
}

function roundedPolygon(points, radius) {
  let d = '';
  points.forEach((p, i) => {
    const a = points[(i - 1 + points.length) % points.length];
    const b = points[(i + 1) % points.length];
    const toA = [a[0] - p[0], a[1] - p[1]];
    const toB = [b[0] - p[0], b[1] - p[1]];
    const la = Math.hypot(...toA);
    const lb = Math.hypot(...toB);
    const s = [p[0] + (toA[0] / la) * radius, p[1] + (toA[1] / la) * radius];
    const e = [p[0] + (toB[0] / lb) * radius, p[1] + (toB[1] / lb) * radius];
    d += `${i ? 'L' : 'M'} ${r2(s[0])} ${r2(s[1])} Q ${r2(p[0])} ${r2(p[1])} ${r2(e[0])} ${r2(e[1])} `;
  });
  return `${d}Z`;
}

/** The head: a pointy-top hexagon with rounded corners around (60, 66). */
export function cellPath(species) {
  const sp = SPECIES[species] || SPECIES.robotnica;
  const points = [-90, -30, 30, 90, 150, 210].map((deg) => {
    const a = (deg * Math.PI) / 180;
    return [60 + sp.R * Math.cos(a), 66 + sp.R * Math.sin(a)];
  });
  return roundedPolygon(points, sp.round);
}

const point = (x, y, rx, ry, deg) => {
  const a = (deg * Math.PI) / 180;
  return [r2(x + rx * Math.cos(a)), r2(y + ry * Math.sin(a))];
};
// The part of an eye below a lid that runs from the left angle to the right one (y down).
const below = (x, y, rx, ry, left, right) => {
  const [x1, y1] = point(x, y, rx, ry, left);
  const [x2, y2] = point(x, y, rx, ry, right);
  return `<path d="M ${x1} ${y1} A ${rx} ${ry} 0 ${left - right > 180 ? 1 : 0} 0 ${x2} ${y2} Z" fill="${HONEY}"/>`;
};

/** One golden eye in the visor; `side` -1 is the bee's right (our left). */
function eye(x, side, state, blink) {
  const y = 58.5;
  const rx = 5.4;
  const ry = 6.8;
  const shut = (d, w) => `<path d="${d}" fill="none" stroke="${HONEY}" stroke-width="${w}" stroke-linecap="round"/>`;
  if (state === 'done') return shut(`M ${r2(x - rx)} ${r2(y + ry * 0.35)} Q ${x} ${r2(y - ry * 1.15)} ${r2(x + rx)} ${r2(y + ry * 0.35)}`, 4);
  if (state === 'sleep' || blink) return shut(`M ${r2(x - rx)} ${y} Q ${x} ${r2(y + ry * 0.7)} ${r2(x + rx)} ${y}`, 3.2);
  if (state === 'focus') return below(x, y, rx, ry, 180, 0);
  if (state === 'work') return `<g class="bb-bee-eye">${below(x, y, rx, ry, 200.5, -20.5)}</g>`;
  if (state === 'fail') return side < 0 ? below(x, y, rx, ry, 165, -52) : below(x, y, rx, ry, 232, 15);
  const k = state === 'needs' ? 1.12 : 1;
  return `<g class="bb-bee-eye"><ellipse cx="${x}" cy="${y}" rx="${r2(rx * k)}" ry="${r2(ry * k)}" fill="${HONEY}"/></g>`;
}

const ACCESSORY_FRAME = { cx: 60, cy: 58.5, r: 26, eyes: [48, 72], neck: [60, 101], flower: [83, 41] };

function accessorySvg(name, color, palette) {
  const { cx, cy, r } = ACCESSORY_FRAME;
  const tone = mix(color, '#000000', 0.5);
  if (name === 'crown') {
    const t = cy - r * 0.92;
    const p = [[-0.46, 0.12], [-0.52, -0.36], [-0.22, -0.14], [0, -0.5], [0.22, -0.14], [0.52, -0.36], [0.46, 0.12]]
      .map(([u, v], i) => `${i ? 'L' : 'M'} ${r2(cx + u * r)} ${r2(t + v * r)}`).join(' ');
    return `<path d="${p} Z" fill="${HONEY}" stroke="#8a5a0a" stroke-width="1.3" stroke-linejoin="round"/>`;
  }
  if (name === 'beret') {
    const x = r2(cx + 0.1 * r);
    const y = r2(cy - 0.86 * r);
    return `<ellipse cx="${x}" cy="${y}" rx="${r2(0.8 * r)}" ry="${r2(0.28 * r)}" transform="rotate(-12 ${x} ${y})" fill="${tone}"/>`
      + `<circle cx="${r2(cx + 0.2 * r)}" cy="${r2(cy - 1.13 * r)}" r="${r2(0.1 * r)}" fill="${tone}"/>`;
  }
  if (name === 'headphones') {
    const R = 1.04 * r;
    const w = 0.36 * r;
    const cup = (x) => `<rect x="${r2(x - w / 2)}" y="${r2(cy - 0.12 * r)}" width="${r2(w)}" height="${r2(0.62 * r)}" rx="${r2(w / 2)}" fill="${tone}"/>`;
    return `<path d="M ${r2(cx - R)} ${r2(cy + 0.12 * r)} A ${r2(R)} ${r2(R)} 0 0 1 ${r2(cx + R)} ${r2(cy + 0.12 * r)}" fill="none" stroke="${tone}" stroke-width="${r2(0.13 * r)}" stroke-linecap="round"/>`
      + cup(cx - R) + cup(cx + R);
  }
  if (name === 'glasses') {
    const [x1, x2] = ACCESSORY_FRAME.eyes;
    const R = 9.4;
    return `<g style="stroke:${palette.glass}" fill="none" stroke-width="2"><circle cx="${x1}" cy="${cy}" r="${R}"/><circle cx="${x2}" cy="${cy}" r="${R}"/><path d="M ${x1 + R} ${cy} L ${x2 - R} ${cy}"/></g>`;
  }
  if (name === 'bowtie') {
    const [x, y] = ACCESSORY_FRAME.neck;
    const s = 0.38 * r;
    return `<path d="M ${x} ${y} L ${r2(x - s)} ${r2(y - 0.6 * s)} L ${r2(x - s)} ${r2(y + 0.6 * s)} Z M ${x} ${y} L ${r2(x + s)} ${r2(y - 0.6 * s)} L ${r2(x + s)} ${r2(y + 0.6 * s)} Z" fill="${tone}"/>`;
  }
  if (name === 'flower') {
    const [x, y] = ACCESSORY_FRAME.flower;
    const p = 0.17 * r;
    const petals = [[0, -1], [0.95, -0.31], [0.59, 0.81], [-0.59, 0.81], [-0.95, -0.31]]
      .map(([u, v]) => `<circle cx="${r2(u * p * 1.35)}" cy="${r2(v * p * 1.35)}" r="${r2(p)}" fill="#ffffff"/>`).join('');
    return `<g transform="translate(${x} ${y})">${petals}<circle r="${r2(p * 0.8)}" fill="${HONEY}"/></g>`;
  }
  return '';
}

function extrasSvg(state, palette) {
  if (state === 'needs') {
    return '<g class="bb-bee-bubble"><circle cx="96" cy="24" r="9" fill="#FF8B4A"/><path d="M 96 19.5 L 96 25" stroke="#1A1203" stroke-width="2.8" stroke-linecap="round"/><circle cx="96" cy="29" r="1.6" fill="#1A1203"/></g>';
  }
  if (state === 'fail') {
    return '<g><circle cx="96" cy="24" r="8.5" fill="#EF4444"/><path d="M 96 19.5 L 96 25" stroke="#ffffff" stroke-width="2.6" stroke-linecap="round"/><circle cx="96" cy="28.8" r="1.5" fill="#ffffff"/></g>';
  }
  if (state === 'done') {
    return '<path class="bb-bee-spark" d="M 96 14 Q 96 23 105 23 Q 96 23 96 32 Q 96 23 87 23 Q 96 23 96 14 Z" fill="#FBCD62"/>'
      + '<path class="bb-bee-spark bb-bee-s2" d="M 22 28 Q 22 33 27 33 Q 22 33 22 38 Q 22 33 17 33 Q 22 33 22 28 Z" fill="#FBCD62"/>';
  }
  if (state === 'sleep') {
    return `<g style="fill:${palette.zz}" font-family="sans-serif" font-weight="700"><text class="bb-bee-z" x="90" y="36" font-size="12">z</text><text class="bb-bee-z bb-bee-z2" x="99" y="24" font-size="16">Z</text></g>`;
  }
  return '';
}

/**
 * The parts of one bee as SVG text, in the order they are drawn: the two wings behind,
 * then the back (antennae, head, stripes) and the front (visor, eyes, accessory, marks).
 * `uid` keeps clip ids apart when many bees share a page. `pose` turns the antennae by
 * the state here (for the shell, which has no CSS).
 */
export function beeParts({
  species = 'robotnica', color = HONEY, state = 'idle', accessory, blink = false, palette = PALETTES.css, uid = 'bee', pose = false,
} = {}) {
  // The bot's color is people's input and ends up in SVG text: only #RRGGBB gets in.
  color = /^#[0-9a-fA-F]{6}$/.test(String(color)) ? color : HONEY;
  uid = String(uid).replace(/[^a-zA-Z0-9_-]/g, '');
  const name = SPECIES[species] ? species : 'robotnica';
  const sp = SPECIES[name];
  const st = STATES.includes(state) ? state : 'idle';
  const body = sp.body || color;
  const wing = sp.wingTint
    ? `fill="${color}" fill-opacity="0.38" stroke="${color}" stroke-opacity="0.7" stroke-width="1.2"`
    : `style="fill:${palette.wing};stroke:${palette.wingEdge}" stroke-width="1.3"`;
  const hex = cellPath(name);
  const clip = `bb-cell-${uid}`;
  const band = 30 * sp.t * 1.25;
  const stripes = sp.stripes.map((f) => {
    const y = 72 + f * 26;
    return `<rect x="20" y="${r2(y - band / 2)}" width="80" height="${r2(band)}" fill="${stripeColor(name, color)}"/>`;
  }).join('');
  const turn = pose ? ({ fail: 30, sleep: 20 }[st] || 0) : 0;
  const antenna = (cls, x1, x2, deg) => {
    const [px, py] = PIVOTS[cls === 'bb-bee-al' ? 'antL' : 'antR'];
    return `<g class="bb-bee-ant ${cls}" style="transform-origin:${px}px ${py}px"${deg ? ` transform="rotate(${deg} ${px} ${py})"` : ''}>`
      + `<path d="M ${x1} 41 L ${x2} 22" fill="none" style="stroke:${palette.head}" stroke-width="3.4" stroke-linecap="round"/>`
      + `<circle cx="${x2}" cy="22" r="3.9" fill="${color}"/></g>`;
  };
  const accessoryName = accessory && ACCESSORIES.includes(accessory) ? accessory : (sp.crown ? 'crown' : 'none');
  return {
    species: name,
    state: st,
    accessory: accessoryName,
    defs: `<clipPath id="${clip}"><path d="${hex}"/></clipPath>`,
    wingL: `<ellipse cx="31" cy="38" rx="11" ry="17" transform="rotate(-38 31 38)" ${wing}/>`,
    wingR: `<ellipse cx="89" cy="38" rx="11" ry="17" transform="rotate(38 89 38)" ${wing}/>`,
    back: antenna('bb-bee-al', 53, 46, -turn) + antenna('bb-bee-ar', 67, 74, turn)
      + `<path d="${hex}" fill="${body}"/><g clip-path="url(#${clip})">${stripes}</g>`,
    front: `<rect x="33" y="48" width="54" height="21" rx="10.5" style="fill:${palette.head}"/>`
      + eye(48, -1, st, blink) + eye(72, 1, st, blink)
      + accessorySvg(accessoryName, color, palette)
      + extrasSvg(st, palette),
  };
}

/** The inside of one bee's <svg viewBox="0 0 120 120">, grouped for the CSS motion. */
export function beeInner(spec = {}) {
  const p = beeParts(spec);
  const [bx, by] = PIVOTS.body;
  const [lx, ly] = PIVOTS.wingL;
  const [rx, ry] = PIVOTS.wingR;
  return `<defs>${p.defs}</defs><g class="bb-bee-body" style="transform-origin:${bx}px ${by}px">`
    + `<g class="bb-bee-wing bb-bee-wl" style="transform-origin:${lx}px ${ly}px">${p.wingL}</g>`
    + `<g class="bb-bee-wing bb-bee-wr" style="transform-origin:${rx}px ${ry}px">${p.wingR}</g>`
    + `${p.back}${p.front}</g>`;
}

/** A bot's species until bots can choose one: stable, from its id. */
function hashOf(text) {
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  return hash;
}

export function speciesForBot(bot) {
  return BOT_SPECIES[hashOf(String(bot?.id || bot?.name || '')) % BOT_SPECIES.length];
}

/**
 * Species for all of a person's bots at once, so that two bots side by side are not the
 * same bee while there are species left. In the order the bots were made: a new bot
 * takes what is free and the older bees keep theirs.
 */
export function speciesForBots(bots = []) {
  const map = {};
  const used = new Set();
  const ordered = [...bots].sort((a, b) => String(a?.created_at || '').localeCompare(String(b?.created_at || ''))
    || String(a?.id || '').localeCompare(String(b?.id || '')));
  ordered.forEach((bot) => {
    if (used.size >= BOT_SPECIES.length) used.clear();
    let i = hashOf(String(bot?.id || bot?.name || '')) % BOT_SPECIES.length;
    while (used.has(BOT_SPECIES[i])) i = (i + 1) % BOT_SPECIES.length;
    used.add(BOT_SPECIES[i]);
    map[bot?.id] = BOT_SPECIES[i];
  });
  return map;
}
